import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import ReactPaginate from 'react-paginate';
import tr from 'date-fns/locale/tr';
import { getParlamentDecisions } from '../../api/councilService';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';

const formatDecisionDate = (dateString) => {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd.MM.yyyy');
};

const ITEMS_PER_PAGE = 10;

function Council() {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const fetchDecisions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getParlamentDecisions(
        currentPage + 1,
        ITEMS_PER_PAGE,
        searchTerm,
        startDate,
        endDate
      );

      if (response.isSuccess && response.data?.items) {
        const mappedDecisions = response.data.items.map((item) => ({
          id: item.guid,
          date: formatDecisionDate(item.date),
          decisionNumber: item.decisionsNo || '-',
          summary: item.header,
          urlTag: item.urlTag
        }));

        setDecisions(mappedDecisions);
        setPageCount(
          response.data.pageCount || Math.ceil((response.data.totalCount || mappedDecisions.length) / ITEMS_PER_PAGE)
        );
      } else {
        setDecisions([]);
        setPageCount(0);
        setError('Kararlar yüklenirken bir hata oluştu');
      }
    } catch (err) {
      setDecisions([]);
      setPageCount(0);
      setError('Kararlar yüklenirken bir hata oluştu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, startDate, endDate]);

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(0);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <Link
        to={`/meclis-kararlari/${rowData.urlTag || rowData.id}`}
        state={{ guid: rowData.id }}
        className="btn btn-primary btn-sm"
      >
        Detaya Git
      </Link>
    );
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };

  return (
    <div className="council-decisions">
      <Helmet>
        <title>Meclis Kararları | İskenderun Belediyesi</title>
        <meta name="description" content="İskenderun Belediyesi meclis kararları listesi." />
      </Helmet>

      <div className="container pb-5">
        <h1 className="page-title text-center mt-4 mb-4">Meclis Kararları</h1>
        <div className="news-tabs-filter">
          <div className="news-tabs-filter-left">
            <div className="news-tabs-filter-left-item">
              <input
                type="text"
                placeholder="Kararlarda Ara"
                value={searchTerm}
                onChange={handleSearch}
              />
              <i className="pi pi-search"></i>
            </div>
          </div>
          <div className="news-tabs-filter-right">
            <div className="news-tabs-filter-right-item">
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                locale={tr}
                className="form-control"
                placeholderText="Tarih Aralığı"
                dateFormat="dd.MM.yyyy"
                isClearable
              />
              <i className="pi pi-calendar"></i>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            <DataTable value={decisions} className="council-table">
              <Column field="date" header="Tarih" style={{ width: '15%' }}></Column>
              <Column field="decisionNumber" header="Karar No" style={{ width: '15%' }}></Column>
              <Column field="summary" header="Karar Özeti" style={{ width: '55%' }}></Column>
              <Column body={actionBodyTemplate} header="" style={{ width: '15%' }}></Column>
            </DataTable>

            {pageCount > 1 && (
              <ReactPaginate
                previousLabel="<"
                nextLabel=">"
                breakLabel="..."
                pageCount={pageCount}
                forcePage={currentPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName="pagination"
                activeClassName="active"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Council;
